import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as scrumApi from '../services/dailyScrumService';

export const scrumKeys = {
  list: (projectId: string, sprintId: string) =>
    ['scrums', projectId, sprintId] as const,
  detail: (projectId: string, sprintId: string, id: string) =>
    ['scrums', projectId, sprintId, id] as const,
  previous: (projectId: string, sprintId: string, date: string) =>
    ['scrums', projectId, sprintId, 'previous', date] as const,
};

export function useDailyScrumList(projectId: string, sprintId: string) {
  return useQuery({
    queryKey: scrumKeys.list(projectId, sprintId),
    queryFn: () => scrumApi.fetchDailyScrums(projectId, sprintId),
    enabled: !!projectId && !!sprintId,
  });
}

export function useDailyScrum(
  projectId: string,
  sprintId: string,
  dailyScrumId: string,
) {
  return useQuery({
    queryKey: scrumKeys.detail(projectId, sprintId, dailyScrumId),
    queryFn: () => scrumApi.fetchDailyScrum(projectId, sprintId, dailyScrumId),
    enabled: !!projectId && !!sprintId && !!dailyScrumId,
  });
}

export function usePreviousScrum(
  projectId: string,
  sprintId: string,
  date: string,
) {
  return useQuery({
    queryKey: scrumKeys.previous(projectId, sprintId, date),
    queryFn: () => scrumApi.fetchPreviousScrum(projectId, sprintId, date),
    enabled: !!projectId && !!sprintId && !!date,
  });
}

export function useCreateDailyScrum(projectId: string, sprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => scrumApi.createDailyScrum(projectId, sprintId, date),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: scrumKeys.list(projectId, sprintId),
      });
    },
  });
}

// --- Entry mutations ---

export function useUpdateEntry(projectId: string, sprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      dailyScrumId: string;
      entryId: string;
      data: { yesterday?: unknown[]; today?: unknown[]; blockers?: unknown[] };
    }) =>
      scrumApi.updateEntry(
        projectId,
        sprintId,
        params.dailyScrumId,
        params.entryId,
        params.data,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: scrumKeys.detail(projectId, sprintId, vars.dailyScrumId),
      });
    },
  });
}

export function useAddEntry(projectId: string, sprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { dailyScrumId: string; userId: string }) =>
      scrumApi.addEntry(
        projectId,
        sprintId,
        params.dailyScrumId,
        params.userId,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: scrumKeys.detail(projectId, sprintId, vars.dailyScrumId),
      });
    },
  });
}

export function useRemoveEntry(projectId: string, sprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { dailyScrumId: string; entryId: string }) =>
      scrumApi.removeEntry(
        projectId,
        sprintId,
        params.dailyScrumId,
        params.entryId,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: scrumKeys.detail(projectId, sprintId, vars.dailyScrumId),
      });
    },
  });
}

export function useRemoveEntryFromFutureScrums(projectId: string, sprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { userId: string; fromDate: string }) =>
      scrumApi.removeEntryFromFutureScrums(projectId, sprintId, params.userId, params.fromDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scrumKeys.list(projectId, sprintId) });
    },
  });
}

// CHG-130 REQ-DHR-017: useConvertScrumToDocument 제거됨
// 데일리 스크럼 회의록은 23:00 KST 스케줄러가 자동으로 저장한다.

// --- SharedItem mutations ---

export function useAddSharedItem(projectId: string, sprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      dailyScrumId: string;
      data: { type: string; content: string };
    }) =>
      scrumApi.addSharedItem(
        projectId,
        sprintId,
        params.dailyScrumId,
        params.data,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: scrumKeys.detail(projectId, sprintId, vars.dailyScrumId),
      });
    },
  });
}

export function useUpdateSharedItem(projectId: string, sprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      dailyScrumId: string;
      itemId: string;
      data: { content?: string; status?: string };
    }) =>
      scrumApi.updateSharedItem(
        projectId,
        sprintId,
        params.dailyScrumId,
        params.itemId,
        params.data,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: scrumKeys.detail(projectId, sprintId, vars.dailyScrumId),
      });
    },
  });
}

export function useDeleteSharedItem(projectId: string, sprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { dailyScrumId: string; itemId: string }) =>
      scrumApi.deleteSharedItem(
        projectId,
        sprintId,
        params.dailyScrumId,
        params.itemId,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: scrumKeys.detail(projectId, sprintId, vars.dailyScrumId),
      });
    },
  });
}
